import { useState, useCallback, useRef } from 'react';
import type { WidgetState, WidgetConfig, SessionInfo, WidgetInitResponse } from '../types';
import { ApiService, ApiError } from '../services/api';
import { getVisitorId } from '../services/storage';

interface UseWidgetReturn {
  state: WidgetState;
  initialize: (config: WidgetConfig) => Promise<void>;
  openWidget: () => void;
  closeWidget: () => void;
  toggleWidget: () => void;
  updateSession: (conversationId: string, sessionId: string) => void;
  resetError: () => void;
  error: string | null;
}

const initialState: WidgetState = {
  isInitialized: false,
  isOpen: false,
  isLoading: false,
  config: null,
  session: null,
  error: null,
};

export function useWidget(): UseWidgetReturn {
  const [state, setState] = useState<WidgetState>(initialState);
  const apiServiceRef = useRef<ApiService | null>(null);

  const initialize = useCallback(async (config: WidgetConfig): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // Determine API base URL
      const baseUrl = config.apiBaseUrl ||
        (typeof window !== 'undefined' ? window.location.origin : '');

      // Create API service
      const apiService = new ApiService(baseUrl, config.tenantSlug);
      apiServiceRef.current = apiService;

      // Initialize widget with tenant configuration
      let tenantConfig: WidgetInitResponse;
      try {
        tenantConfig = await apiService.initWidget();
      } catch (error) {
        if (error instanceof ApiError) {
          throw error;
        }
        throw new ApiError('INIT_FAILED', 'Failed to initialize widget');
      }

      // Use visitor_id from backend or local storage
      const visitorId = tenantConfig.visitor_id || getVisitorId();

      // Always use fresh session_id from init (no cross-reload persistence)
      const sessionId = tenantConfig.session_id;

      // Merge tenant branding with config (map snake_case to camelCase)
      const mergedConfig: WidgetConfig = {
        ...config,
        primaryColor: config.primaryColor || tenantConfig.config.primary_color,
        secondaryColor: config.secondaryColor || tenantConfig.config.secondary_color,
        greeting: config.greeting || tenantConfig.config.welcome_message,
        placeholder: config.placeholder || 'Type a message...',
        position: config.position || (tenantConfig.config.button_position as 'bottom-right' | 'bottom-left') || 'bottom-right',
        businessName: config.businessName || tenantConfig.config.business_name,
        stripePublishableKey: tenantConfig.stripe_publishable_key || null,
      };

      // Create session info — always starts fresh (no stored conversation)
      const session: SessionInfo = {
        visitorId,
        conversationId: null,
        sessionId: sessionId,
        tenantSlug: config.tenantSlug,
      };

      setState({
        isInitialized: true,
        isOpen: config.autoOpen ?? false,
        isLoading: false,
        config: mergedConfig,
        session,
        error: null,
      });
    } catch (error) {
      const errorMessage = error instanceof ApiError
        ? error.message
        : 'Failed to initialize widget';

      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
    }
  }, []);

  const openWidget = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: true,
    }));
  }, []);

  const closeWidget = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: false,
    }));
  }, []);

  const toggleWidget = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
    }));
  }, []);

  const resetError = useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: null,
    }));
  }, []);

  // Update session when conversation is started (React state only, no localStorage)
  const updateSession = useCallback(
    (conversationId: string, sessionId: string) => {
      setState((prev) => ({
        ...prev,
        session: prev.session
          ? { ...prev.session, conversationId, sessionId }
          : null,
      }));
    },
    []
  );

  return {
    state,
    initialize,
    openWidget,
    closeWidget,
    toggleWidget,
    updateSession,
    resetError,
    error: state.error,
  };
}

// Export for context usage
export type { UseWidgetReturn };
